import React, { useState, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Paper } from '@mui/material';
import { Checkbox, Table as RsuiteTable } from 'rsuite';
import { dynamicSort } from '../../../../../../common-client2/src/services/sort';
import { useAvailableHeight } from '../../../../../../common-client2/src/hooks';
import RequestTypeChip from '../RequestTypeChip';
import { ListViewShiftCardWithTooltip } from '../ListViewShiftCardWithTooltip';
import * as Helpers from '../BillboardListView.helpers';
import { employeeDashboardStore as store } from '../../../../../store';
import { updateSnackbar } from '../../../../actions';
import './Table.scss';
import { RequestSubtypes, ListViewRequestTypes } from '../../../../../Enums';


const { Cell, Column, HeaderCell } = RsuiteTable;

/** @type {CommonClient2.SortType} */
let defaultSortType;
 
defaultSortType = 'asc';
const rowKey = "listViewId";

const CheckCell = ({
    rowData, isRowSelectionDisabled, onChange, checkedKeys, dataKey, ...props
}) => (
    <Cell
        {...props}
        style={{ padding: 0 }}
    >
        <div style={{ lineHeight: '46px' }}>
            {rowData.isCheckable ? (
                <Checkbox
                    value={rowData[dataKey]}
                    inline
                    onChange={onChange}
                    disabled={!!isRowSelectionDisabled}
                    checked={checkedKeys.some((item) => item === rowData[dataKey])}
                />
                ) : (<></>)}
            
        </div>
    </Cell>
);

/**
 * @param {{
*  listItems: IListViewItem[];
*  monthlySchedule: *[];
*  handleShiftClick?: (rowData: Helpers.IListViewItem, event: *) => void;
*  handleMultiSelectShift?: (rowData: Helpers.IListViewItem, event: *) => void;
*  handleMultiSelectOpenShift?: (rowData: Helpers.IListViewItem, event: *) => void;
*  checkedKeys: string[];
*  isRowSelectionDisabled?: boolean;
*  fillAvailableHeight: boolean;
*  height?: number;
*  isDisplaySegmentInfos?: boolean;
* } & import('rsuite-table').TableProps} param0
*/
const Table = ({
    listItems,
    monthlySchedule,
    handleShiftClick = () => {},
    handleMultiSelectShift = () => {},
    handleMultiSelectOpenShift = () => {},
    checkedKeys,
    isRowSelectionDisabled,
    fillAvailableHeight,
    height,
    isDisplaySegmentInfos = false,
}) => {
    const { t } = useTranslation('employmentBillboard', { keyPrefix: 'billboard.listView' });
    const ref = useRef(null);
    const [sortType, setSortType] = useState(defaultSortType);
    const [sortColumn, setSortColumn] = useState('date');
    const sortKey = sortType === 'desc' ? `-${sortColumn}` : sortColumn;
    const sortedData = useMemo(() => listItems.sort(dynamicSort(sortKey)), [JSON.stringify(listItems), sortType, sortColumn]);
    const autoHeightValue = useAvailableHeight(ref, fillAvailableHeight);

    const checked = checkedKeys.length === sortedData?.length;
    const indeterminate = !!checkedKeys.length && checkedKeys.length < sortedData?.length;

    /**
     * @param {string} newSortColumn
     * @param {CommonClient2.SortType} newSortType
     */
    const handleSortColumn = (newSortColumn, newSortType) => {
        setSortColumn(newSortColumn);
        setSortType(newSortType);
    };

    const handleCheckAll = (_, checked) => {
        const rows = checked ? sortedData.filter(x => x.isCheckable) : [];
        let handleMultiSelect;
        if(!rows.length){
            return;
        }
        else if (rows.every(x => x.type === ListViewRequestTypes.myShift)){
            handleMultiSelect = (monthlyViewOffer) => handleMultiSelectShift(monthlyViewOffer);
        }
        else if (rows.every(x => x.type === ListViewRequestTypes.openShift)){
            handleMultiSelect = (monthlyViewOffer) => handleMultiSelectOpenShift(monthlyViewOffer);
        } else {
            store.dispatch(updateSnackbar({
                typeSnackbar: 'error',
                messageSnackbar: t('errors.selectAllSameType'),
                openSnackbar: true,
            }));
            return;
        }
        for (const row of rows){
            const monthlyViewOffer = Helpers.getMonthlyViewOfferModel(monthlySchedule, row);
            handleMultiSelect(monthlyViewOffer);
        }
    };
    
    return (
        <Paper 
            elevation={2}
            ref={ref}
            className="listView-core--list-view-container"
        >
            <RsuiteTable
                data={sortedData}
                rowKey={rowKey}
                sortType={sortType}
                sortColumn={sortColumn}
                // @ts-ignore
                onSortColumn={handleSortColumn}
                rowClassName={(rowData) => `listView-core--table-dataview-row ${rowData?.isAwarded ? 'awarded' : ''}`}
                height={height || autoHeightValue}
                //autoHeight
                wordWrap={"break-word"}
                onRowClick={async (rowData, e) => {
                    if (rowData.type === ListViewRequestTypes.myPaycode) {
                        return;
                    }
                    const { firstChild } = e.target;
                    const monthlyViewOffer = Helpers.getMonthlyViewOfferModel(monthlySchedule, rowData);
                    if (firstChild?.type === "checkbox") {
                        if (rowData.type === ListViewRequestTypes.myShift) {
                            handleMultiSelectShift(monthlyViewOffer);
                        }
                        else if (rowData.type === ListViewRequestTypes.openShift) {
                            handleMultiSelectOpenShift(monthlyViewOffer);
                        }
                        return;
                    }
                    handleShiftClick({schedule: monthlyViewOffer});
                }}
                shouldUpdateScroll={false}
            >
                <Column
                    width={50}
                    align="center"
                >
                    <HeaderCell className="header-checkbox-cell" >
                        <Checkbox
                            inline
                            checked={checked}
                            indeterminate={indeterminate}
                            disabled={!!isRowSelectionDisabled}
                            onChange={handleCheckAll}
                        />
                    </HeaderCell>
                    <CheckCell
                        dataKey={rowKey}
                        checkedKeys={checkedKeys}
                        isRowSelectionDisabled={isRowSelectionDisabled}
                        rowData={null}
                    />
                </Column>
                <Column
                    sortable
                    flexGrow={1}
                >
                    <HeaderCell>{t('headers.date')}</HeaderCell>
                    <Cell dataKey="dateLabel"/>
                </Column>
                <Column
                    sortable
                    flexGrow={3}
                >
                    <HeaderCell>{t('headers.name')}</HeaderCell>
                    <Cell dataKey="type" className="listView-core--name-cell">
                        {(rowData)=>(
                            <ListViewShiftCardWithTooltip
                                isVto={rowData.type === ListViewRequestTypes.voluntaryTimeOff}
                                isOpenShift={rowData.type === ListViewRequestTypes.openShift}
                                shift={rowData.shift}
                                partialShift={rowData.isPartial ? rowData.partialShift : null}
                                requestType={rowData.type}
                                isSignUp={rowData.subtype === RequestSubtypes.signUp}
                                isDisplaySegmentInfos={isDisplaySegmentInfos}
                            />)}
                    </Cell>
                </Column>
                <Column
                    sortable
                    flexGrow={2}
                >
                    <HeaderCell>{t('headers.type')}</HeaderCell>
                    <Cell dataKey="type" className="listView-core--type-cell">
                        {(rowData)=>(<RequestTypeChip listItem={rowData} />)}
                    </Cell>
                </Column>
                <Column
                    sortable 
                    flexGrow={1}
                >
                    <HeaderCell>{t('headers.owner')}</HeaderCell>
                    <Cell dataKey="owner"/>
                </Column>
                <Column
                    sortable
                    flexGrow={2}
                >
                    <HeaderCell>{t('headers.job')}</HeaderCell>
                    <Cell dataKey="job"/>
                </Column>
                <Column
                    sortable
                    flexGrow={2}
                >
                    <HeaderCell>{t('headers.location')}</HeaderCell>
                    <Cell dataKey="location" >
                        {(rowData)=>(<div className="listView-core--location-container">{rowData.location}</div>)}
                    </Cell>
                </Column>
            </RsuiteTable>
        </Paper>
    );
};
export default Table;